import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { type KpiData } from './types';
import { ThemePalette } from 'mui.theme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface KpiCardProps {
  label: string;
  value: string;
  color?: string;
  enableDarkTheme: boolean;
}

const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  color = '#fff',
  enableDarkTheme,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const cardBg = enableDarkTheme
    ? palette.boxBackground
    : palette.boxBackground;
  const border = palette.cardBorderColor;
  const labelColor = enableDarkTheme ? '#6b7280' : '#8A92A0';

  return (
    <Box
      sx={{
        background: cardBg,
        border: `1px solid ${border}`,
        borderRadius: '8px',
        padding: '10px 14px',
        minWidth: '148px',
        flexShrink: 0,
        backdropFilter: 'blur(10px)',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: 'rgba(99,102,241,0.4)' },
      }}
    >
      <Typography
        sx={{
          fontSize: '9px',
          fontWeight: 600,
          color: labelColor,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          mb: '4px',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: '15px', fontWeight: 700, color, lineHeight: 1.2 }}
      >
        {value}
      </Typography>
    </Box>
  );
};

interface Props {
  kpis: KpiData;
  enableDarkTheme: boolean;
  activeTrucks: number;
  totalHaulTrucks: number;
}

const KpiBar: React.FC<Props> = ({
  kpis,
  enableDarkTheme,
  activeTrucks,
  totalHaulTrucks,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const barBg = enableDarkTheme
    ? palette.toolBarBackground
    : palette.toolBarBackground;
  const barBorder = palette.cardBorderColor;

  return (
    <Box
      sx={{
        background: barBg,
        borderBottom: `1px solid ${barBorder}`,
        padding: '6px 16px',
        overflowX: 'auto',
        overflowY: 'hidden',
        flexShrink: 0,
        '&::-webkit-scrollbar': { height: '4px' },
        '&::-webkit-scrollbar-thumb': {
          background: barBorder,
          borderRadius: '2px',
        },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
      }}
    >
      <Box sx={{ display: 'flex', gap: '10px', minWidth: 'fit-content' }}>
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_actual_target_production')}
          value={`${kpis.production.actual.toLocaleString()} / ${kpis.production.target.toLocaleString()} t`}
          color="#a5b4fc"
        />
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_avg_efficiency')}
          value={`${kpis.efficiency.value} ${kpis.efficiency.unit}`}
          color="#4ade80"
        />
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_trips_done_target')}
          value={`${kpis.trips.done} / ${kpis.trips.target}`}
          color="#fbbf24"
        />
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_trucks_active')}
          value={`${activeTrucks} / ${totalHaulTrucks}`}
          color="#4ade80"
        />
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_active_alerts')}
          value="–"
          color="#f87171"
        />
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_total_fuel_used')}
          value="–"
          color="#67e8f9"
        />
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_utilization')}
          value="–"
          color="#f472b6"
        />
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_avg_cycle_time')}
          value="–"
          color={enableDarkTheme ? '#fff' : '#1a202c'}
        />
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_cost_per_ton')}
          value="–"
          color="#fbbf24"
        />
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_payload_compliance')}
          value="–"
          color="#4ade80"
        />
        <KpiCard
          enableDarkTheme={enableDarkTheme}
          label={t('dm_kpi_active_delays')}
          value="–"
          color="#f87171"
        />
      </Box>
    </Box>
  );
};

export default KpiBar;
